import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Pencil, Trash2, Plus, ExternalLink } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function AdminList() {
    const [posts, setPosts] = useState(null);
    const [busy, setBusy] = useState(null); // id being deleted

    const load = async () => {
        try {
            const { data } = await api.get("/admin/posts");
            setPosts(data);
        } catch (err) {
            toast.error(formatApiError(err));
            setPosts([]);
        }
    };

    useEffect(() => {
        document.title = "Posts | Chesterton Admin";
        load();
    }, []);

    const onDelete = async (post) => {
        if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
        setBusy(post.id);
        try {
            await api.delete(`/admin/posts/${post.id}`);
            toast.success("Post deleted.");
            setPosts((prev) => prev.filter((p) => p.id !== post.id));
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10" data-testid="admin-list-page">
            <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.28em] text-acre-muted mb-3">Content</div>
                    <h1 className="font-serif-display text-3xl lg:text-4xl !text-acre-ink leading-tight">Blog posts</h1>
                </div>
                <Link
                    to="/admin/new"
                    data-testid="admin-new-post-cta"
                    className="inline-flex items-center gap-2 bg-acre-primary text-white px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-acre-primaryHover transition-colors"
                >
                    <Plus size={14} /> New post
                </Link>
            </div>

            {posts === null ? (
                <div className="text-acre-muted text-sm py-16 text-center" data-testid="admin-list-loading">Loading…</div>
            ) : posts.length === 0 ? (
                <div className="border border-dashed border-acre-line py-20 text-center" data-testid="admin-list-empty">
                    <div className="text-acre-muted text-sm mb-6">No posts yet.</div>
                    <Link
                        to="/admin/new"
                        className="inline-flex items-center gap-2 bg-acre-primary text-white px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-acre-primaryHover transition-colors"
                    >
                        <Plus size={14} /> Create your first post
                    </Link>
                </div>
            ) : (
                <div className="bg-white border border-acre-line" data-testid="admin-posts-table">
                    <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-acre-line text-[10px] uppercase tracking-[0.22em] text-acre-muted">
                        <div className="col-span-5">Title</div>
                        <div className="col-span-2">Category</div>
                        <div className="col-span-2">Date</div>
                        <div className="col-span-1">Status</div>
                        <div className="col-span-2 text-right">Actions</div>
                    </div>
                    <ul className="divide-y divide-acre-line">
                        {posts.map((p) => (
                            <li
                                key={p.id}
                                data-testid={`admin-post-row-${p.slug}`}
                                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-[#FBFAF8] transition-colors"
                            >
                                <div className="md:col-span-5">
                                    <div className="text-acre-ink font-medium leading-snug">{p.title}</div>
                                    <div className="text-xs text-acre-muted mt-1">/{p.slug}</div>
                                </div>
                                <div className="md:col-span-2 text-sm text-acre-muted capitalize">
                                    {(p.category || "").replace(/-/g, " ")}
                                </div>
                                <div className="md:col-span-2 text-sm text-acre-muted">{formatDate(p.date)}</div>
                                <div className="md:col-span-1">
                                    <span
                                        className={`inline-block text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${
                                            p.published
                                                ? "text-acre-primary border-acre-primary"
                                                : "text-acre-muted border-acre-line"
                                        }`}
                                    >
                                        {p.published ? "Live" : "Draft"}
                                    </span>
                                </div>
                                <div className="md:col-span-2 flex items-center gap-2 md:justify-end">
                                    {p.published && (
                                        <a
                                            href={`/blog/${p.slug}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`admin-view-${p.slug}`}
                                            title="View live"
                                            className="p-2 border border-acre-line text-acre-ink hover:border-acre-primary hover:text-acre-primary transition-colors"
                                        >
                                            <ExternalLink size={14} />
                                        </a>
                                    )}
                                    <Link
                                        to={`/admin/edit/${p.id}`}
                                        data-testid={`admin-edit-${p.slug}`}
                                        title="Edit"
                                        className="p-2 border border-acre-line text-acre-ink hover:border-acre-primary hover:text-acre-primary transition-colors"
                                    >
                                        <Pencil size={14} />
                                    </Link>
                                    <button
                                        onClick={() => onDelete(p)}
                                        disabled={busy === p.id}
                                        data-testid={`admin-delete-${p.slug}`}
                                        title="Delete"
                                        className="p-2 border border-acre-line text-acre-ink hover:border-acre-secondary hover:text-acre-secondary transition-colors disabled:opacity-40"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
